import L from 'leaflet';

export function isPointInPolygon(point: number[], polygon: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0]; const
      yi = polygon[i][1];
    const xj = polygon[j][0]; const
      yj = polygon[j][1];
    const intersect = ((yi > point[1]) !== (yj > point[1]))
          && (point[0] < ((xj - xi) * (point[1] - yi) / (yj - yi)) + xi);
    if (intersect) {inside = !inside;}
  }
  return inside;
}

export function getPolygonCentroid(vertices: number[][]): { lat: number, lng: number } {
  function shrinkPolygon(polygon: number[][], factor: number): number[][] {
    const result: number[][] = [];
    const n = polygon.length;
    for (let i = 0; i < n; i++) {
      const prev = polygon[(i - 1 + n) % n];
      const curr = polygon[i];
      const next = polygon[(i + 1) % n];

      const dx1 = curr[0] - prev[0];
      const dy1 = curr[1] - prev[1];
      const dx2 = next[0] - curr[0];
      const dy2 = next[1] - curr[1];

      const nx1 = -dy1;
      const ny1 = dx1;
      const nx2 = -dy2;
      const ny2 = dx2;

      const mx = (nx1 + nx2) / 2;
      const my = (ny1 + ny2) / 2;
      const length = Math.sqrt((mx ** 2) + (my ** my));

      result.push([
        curr[0] + ((factor * mx) / length),
        curr[1] + ((factor * my) / length),
      ]);
    }
    return result;
  }

  function polygonArea(polygon: number[][]): number {
    let area = 0;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      area += (polygon[j][0] + polygon[i][0]) * (polygon[j][1] - polygon[i][1]);
    }
    return Math.abs(area / 2);
  }

  function weightedAverageCentroid(polygon: number[][]): number[] {
    let centerX = 0;
    let centerY = 0;
    let totalArea = 0;

    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      const area = (xi * yj) - (xj * yi);
      totalArea += area;

      centerX += (xi + xj) * area;
      centerY += (yi + yj) * area;
    }

    if (totalArea === 0) {
      // Fallback to arithmetic mean if the polygon has no area
      centerX = polygon.reduce((sum, v) => sum + v[0], 0) / polygon.length;
      centerY = polygon.reduce((sum, v) => sum + v[1], 0) / polygon.length;
    } else {
      const factor = 1 / (3 * totalArea);
      centerX *= factor;
      centerY *= factor;
    }

    return [centerX, centerY];
  }

  const originalArea = polygonArea(vertices);
  let currentPolygon = vertices;
  let centerPoint: number[] = [0, 0];

  const shrinkFactor = Math.sqrt(originalArea) * 0.01;
  const minArea = originalArea * 0.0001;
  const maxIterations = 100; // Maximum number of shrinkage iterations

  let iterations = 0;
  while (polygonArea(currentPolygon) > minArea && iterations < maxIterations) {
    const shrunkPolygon = shrinkPolygon(currentPolygon, shrinkFactor);

    const avgX = shrunkPolygon.reduce((sum, v) => sum + v[0], 0) / shrunkPolygon.length;
    const avgY = shrunkPolygon.reduce((sum, v) => sum + v[1], 0) / shrunkPolygon.length;
    centerPoint = [avgX, avgY];

    if (isPointInPolygon(centerPoint, vertices)) {
      break;
    }

    currentPolygon = shrunkPolygon;
    iterations++;
  }

  // If shrinkage algorithm didn't find a suitable point, use weighted average
  if (!isPointInPolygon(centerPoint, vertices) || iterations >= maxIterations) {
    centerPoint = weightedAverageCentroid(vertices);
  }

  // Final fallback if the calculated point is still not in the polygon
  if (!isPointInPolygon(centerPoint, vertices)) {
    centerPoint = [
      vertices.reduce((sum, v) => sum + v[0], 0) / vertices.length,
      vertices.reduce((sum, v) => sum + v[1], 0) / vertices.length,
    ];
  }

  return { lng: centerPoint[0], lat: centerPoint[1] };
}

export function lambert93ToWGS84(x: number, y: number): { lat: number; lng: number } {
  const e = 0.0818191910428158; // Eccentricity of the Earth's ellipsoid
  const n = 0.7256077650532670; // Projection constant
  const C = 11754255.4261; // Projection constant
  const Xs = 700000.0; // False easting
  const Ys = 12655612.0499; // False northing
  const lon0 = 3.0; // Longitude of the origin (central meridian)

  const r = Math.sqrt(((x - Xs) ** 2) + ((y - Ys) ** 2));
  const gamma = Math.atan2(x - Xs, Ys - y);
  const latiso = -Math.log(r / C) / n;

  // Iteratively calculate latitude
  let phi = (2 * Math.atan(Math.exp(latiso))) - (Math.PI / 2);
  let phiPrev = 0;
  const precision = 1e-11; // Precision for iteration
  while (Math.abs(phi - phiPrev) > precision) {
    phiPrev = phi;
    phi = (2 * Math.atan(
      (
        (
          (1 + (e * Math.sin(phiPrev))) 
						/ 
					(1 - (e * Math.sin(phiPrev)))
        ) ** (e / 2)
      ) * Math.exp(latiso),
    )) - (Math.PI / 2);
  }

  const lat = (phi * 180) / Math.PI; // Convert radians to degrees
  const lon = lon0 + ((gamma / n) * (180 / Math.PI)); // Convert radians to degrees

  return { lat, lng: lon };
}

export const rgpdBlueDarkIcon = (color: string | null = null) => new L.DivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" class="size-10 ${color ? `" style="color:${color}"` : 'text-rgpd-blue-dark'}" opacity="0.9">
    <path fill="currentColor" stroke="white" fill-rule="evenodd" d="M11.291 21.706 12 21l-.709.706zM12 21l.708.706a1 1 0 0 1-1.417 0l-.006-.007-.017-.017-.062-.063a47.708 47.708 0 0 1-1.04-1.106 49.562 49.562 0 0 1-2.456-2.908c-.892-1.15-1.804-2.45-2.497-3.734C4.535 12.612 4 11.248 4 10c0-4.539 3.592-8 8-8 4.408 0 8 3.461 8 8 0 1.248-.535 2.612-1.213 3.87-.693 1.286-1.604 2.585-2.497 3.735a49.583 49.583 0 0 1-3.496 4.014l-.062.063-.017.017-.006.006L12 21zm0-8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" clip-rule="evenodd"/>
  </svg>`,
  iconSize: [41, 41],
  iconAnchor: [21, 39],
  popupAnchor: [-1, -31],
  className: 'marker-icon',
});
